import axios from 'axios'
import { json } from 'react-router-dom'

import { useGlobalDataStore } from '@stores/globalData/globalData.store'
import ErrorEmitter from '@utils/emitters/ErrorEmitter'

import type { APIError } from '@providers/APIErrorProvider'
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'

export const axiosInstance = axios.create({
  headers: {
    'X-CSRF-TOKEN': useGlobalDataStore.getState().csrfToken,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

export type HTTPConfig<D = any> = AxiosRequestConfig<D> & {
  hideGlobalError?: boolean
}

export function getAPIErrors(err: AxiosError): APIError[] {
  let errors: APIError[] = []

  if (err.response) {
    if (err.response.data?.errors) {
      errors = err.response.data.errors
    } else {
      const { status, data } = err.response
      if (status === 0)
        errors = [
          {
            code: 'serverNotReachable',
            message: 'The server could not be reached',
          },
        ]
      else if (status === 403)
        errors = [
          {
            code: 'permissionDenied',
            message: 'No permission to access service',
          },
        ]
      else if (status === 404) errors = [{ code: 'notFound', message: 'Location not found' }]
      else if (status >= 400 && status < 500) {
        // parse for error message
        errors = data.errors || data.error || []
        if (!Array.isArray(errors)) errors = [errors]
      } else if (status >= 500)
        errors = [{ code: 'internalServerError', message: 'Internal server error' }]
    }
  }

  return errors
}

export type HTTPResponse<T, D = any> = Promise<[AxiosResponse<T, D> | null, APIError[] | null]>
export type ErrorResponse = { errors: APIError[]; status: number }

async function get<T = any, D = any>(url: string, config?: HTTPConfig<D>): HTTPResponse<T, D> {
  try {
    const resp = await axiosInstance.get<T, AxiosResponse<T>, D>(url, config)
    return [resp, null]
  } catch (err) {
    const errors = getAPIErrors(err as AxiosError)
    if (!config?.hideGlobalError) {
      ErrorEmitter.addErrors(errors)
    }
    return [null, errors]
  }
}

async function post<T = any, D = any>(
  url: string,
  data?: D,
  config?: HTTPConfig<D>
): HTTPResponse<T, D> {
  try {
    const resp = await axiosInstance.post<T, AxiosResponse<T>, D>(url, data, config)
    return [resp, null]
  } catch (err) {
    const errors = getAPIErrors(err as AxiosError)
    if (!config?.hideGlobalError) {
      ErrorEmitter.addErrors(errors)
    }
    return [null, errors]
  }
}

async function loaderGet<T = any, D = any>(
  url: string,
  config?: HTTPConfig<D>
): Promise<T | ErrorResponse> {
  try {
    const resp = await axiosInstance.get<T, AxiosResponse<T>, D>(url, config)
    return resp.data
  } catch (err) {
    const error = err as AxiosError

    const errors = getAPIErrors(error)
    const status = error.response?.status ?? 500
    if (status >= 500) {
      throw json({ errors }, { status })
    }

    // DO NOT throw error, just return errors info
    return { errors, status }
  }
}

async function loaderPost<T = any, D = any>(
  url: string,
  data?: D,
  config?: HTTPConfig<D>
): Promise<T | ErrorResponse> {
  try {
    const resp = await axiosInstance.post<T, AxiosResponse<T>, D>(url, data, config)
    return resp.data
  } catch (err) {
    const error = err as AxiosError

    const errors = getAPIErrors(error)
    const status = error.response?.status ?? 500
    if (status >= 500) {
      throw json({ errors }, { status })
    }

    // DO NOT throw error, just return errors info
    return { errors, status }
  }
}

async function actionGet<T = any, D = any>(
  url: string,
  config?: HTTPConfig<D>
): Promise<T | ErrorResponse> {
  try {
    const resp = await axiosInstance.get<T, AxiosResponse<T>, D>(url, config)
    return resp.data
  } catch (err) {
    const error = err as AxiosError

    const errors = getAPIErrors(error)
    const status = error.response?.status ?? 500
    if (status >= 500) {
      throw json({ errors }, { status })
    }

    if (!config?.hideGlobalError) {
      ErrorEmitter.addErrors(errors)
    }

    // DO NOT throw error, just return errors info
    return { errors, status }
  }
}

async function actionPost<T = any, D = any>(
  url: string,
  data?: D,
  config?: HTTPConfig<D>
): Promise<T | ErrorResponse> {
  try {
    const resp = await axiosInstance.post<T, AxiosResponse<T>, D>(url, data, config)
    return resp.data
  } catch (err) {
    const error = err as AxiosError

    const errors = getAPIErrors(error)
    const status = error.response?.status ?? 500
    if (status >= 500) {
      throw json({ errors }, { status })
    }

    if (!config?.hideGlobalError) {
      ErrorEmitter.addErrors(errors)
    }

    // DO NOT throw error, just return errors info
    return { errors, status }
  }
}

export default {
  get,
  post,
  loader: { get: loaderGet, post: loaderPost },
  action: { get: actionGet, post: actionPost },
}
